import { useUserInfo } from "@/api/user";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ROUTES } from "@/constants/routes";
import { useOpenSettings } from "@/context/OpenSettingsContext";
import { isRealNativePlatform } from "@/native/platform";
import { formatInteger } from "@/utils/currency";
import { useMemo } from "react";

export interface Usage {
  pct: number;
  used: number;
  total: number;
  /** When the period's count resets; undefined where the subscription carries no period end. */
  resetsAt?: Date;
}

/**
 * The user's share of the seat's credits this period, from `/me`. Undefined where there is
 * nothing to show: no subscription or usage on the account, or the real iOS container, where
 * billing/usage UI is hidden (Apple 3.1.1).
 */
export function useUsage(): Usage | undefined {
  const { data: userInfo } = useUserInfo();
  const usage = userInfo?.usage;
  const subscription = userInfo?.subscription;
  return useMemo(() => {
    if (isRealNativePlatform() || !subscription || !usage) return undefined;
    const used = usage.current_period_user_credit_count ?? 0;
    const included = subscription.included_credits_per_seat ?? 0;
    const total = included > 0 ? included : 1;
    const resetsAt = subscription.current_period_end
      ? new Date(subscription.current_period_end)
      : undefined;
    return {
      pct: Math.min(100, Math.round((used / total) * 100)),
      used,
      total,
      resetsAt:
        resetsAt && !Number.isNaN(resetsAt.getTime()) ? resetsAt : undefined,
    };
  }, [usage, subscription]);
}

/** The ring alone: calm, warning past 75, blocked past 90 — the lifecycle vocabulary. */
export function UsageRing({
  pct,
  size = 16,
  stroke = 2.25,
}: {
  pct: number;
  size?: number;
  stroke?: number;
}) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (pct / 100) * c;

  const ringColor =
    pct >= 90
      ? "var(--lc-blocked)"
      : pct >= 75
        ? "var(--lc-warning)"
        : "var(--lc-active)";

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="-rotate-90 shrink-0"
      aria-hidden
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="var(--n-10)"
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={ringColor}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={`${dash} ${c}`}
        className="transition-[stroke-dasharray,stroke] duration-300 ease-out"
      />
    </svg>
  );
}

interface TokenMeterProps {
  size?: number;
  stroke?: number;
}

/** The ring as its own control: a button that opens Usage, the exact numbers on hover. */
export function TokenMeter({ size = 16, stroke = 2.25 }: TokenMeterProps) {
  const usage = useUsage();
  const openSettings = useOpenSettings();

  if (!usage) {
    return null;
  }
  const { pct, used, total } = usage;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={`AI credits — ${pct}% used`}
          data-onboarding-target="footer-usage"
          onClick={() => openSettings(ROUTES.SETTINGS.USAGE)}
          className="inline-flex size-8 shrink-0 items-center justify-center rounded-md outline-none transition-colors hover:bg-n-05 focus-visible:outline-2 focus-visible:outline-rust focus-visible:outline-offset-[-2px]"
        >
          <UsageRing pct={pct} size={size} stroke={stroke} />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" align="end" className="flex flex-col gap-0.5">
        <span className="font-medium">{pct}% used</span>
        <span className="font-mono text-2xs text-n-30">
          {formatInteger(used)} / {formatInteger(total)} credits
        </span>
      </TooltipContent>
    </Tooltip>
  );
}
